/* ==========================================================================
   ToTheLove — Header : repli automatique de la nav en burger
   Chargé defer depuis sections/header.liquid.

   Principe : on mesure la largeur nécessaire de la nav (somme des largeurs
   des items + gouttières) tant qu'elle est visible, et on la compare à la
   place disponible dans sa colonne de grille ((largeur header - logo) / 2,
   grille 1fr auto 1fr du mode middle-center). Si ça ne tient pas, on pose
   .ttl-header--collapsed sur .header : la nav disparaît, le burger Dawn
   (header-drawer) prend le relais. La largeur nécessaire est mémorisée pour
   pouvoir ré-étendre au resize, même quand la nav est masquée.

   Scoping strict : tout part de [data-ttl-nav]. Aucun sélecteur global.

   ---------------------------------------------------------------------------
   S40 — deux corrections de performance, aucun changement de comportement.

   1. `--ttl-announcement-height` n'est plus écrite à chaque événement scroll.
      Une custom property posée sur <html> invalide le style de TOUT le
      document ; le faire à chaque frame de défilement pesait en continu sur
      le budget Style & Layout (295 à 569 ms selon la page dans PageSpeed) et
      c'est exactement le genre de travail qui dégrade l'INP.
      Deux garde-fous cumulés : coalescence en requestAnimationFrame (au plus
      une mesure par frame, quel que soit le nombre d'événements) et garde de
      changement (on n'écrit que si la valeur en pixels a réellement bougé).
      Une fois la bannière sortie du champ, la valeur reste à 0 et plus rien
      n'est écrit du tout.

   2. `update()` ne force plus de reflow synchrone. L'ancienne version faisait
      lecture -> écriture -> lecture -> écriture dans le même tick : le second
      `measureRequired()` après le `classList.toggle` obligeait le navigateur
      à recalculer la mise en page immédiatement (24 ms sur l'accueil, 45 ms
      sur la collection, où le DOM est plus lourd).
      La re-mesure d'après dépliage est désormais reportée au frame suivant :
      c'est le même besoin — la nav redevenue visible doit être remesurée —
      mais sans arracher un layout au milieu du tick.

   Ces deux points ne changent RIEN à ce qui s'affiche : mêmes seuils, même
   BUFFER, même classe, mêmes déclencheurs.
   ========================================================================== */

(function () {
  'use strict';

  var BUFFER = 32; // marge de sécurité (px) avant de replier / ré-étendre
  var COLLAPSED_CLASS = 'ttl-header--collapsed';

  /* ------------------------------------------------------------------
     Hauteur visible de la bannière d'annonce
     ------------------------------------------------------------------ */

  var announcementBar = null;
  var announcementObserver = null;
  var announcementLast = null; // dernière valeur ÉCRITE (null = jamais écrite)
  var announcementQueued = false;

  function readAnnouncementHeight() {
    if (!announcementBar) return 0;
    // Hauteur encore visible dans le viewport (0 une fois scrollée hors écran)
    var rect = announcementBar.getBoundingClientRect();
    return Math.ceil(Math.max(0, Math.min(rect.height, rect.bottom)));
  }

  function commitAnnouncementHeight() {
    var value = readAnnouncementHeight();
    // Garde de changement : pendant la majeure partie d'un défilement la valeur
    // est stable (0 une fois la bannière passée). Sans ce test on réécrivait la
    // même chaîne sur <html> à chaque frame, et on invalidait donc le style du
    // document entier pour rien.
    if (value === announcementLast) return;
    announcementLast = value;
    document.documentElement.style.setProperty('--ttl-announcement-height', value + 'px');
  }

  // Coalescence : quel que soit le nombre d'événements reçus dans le frame,
  // une seule mesure et au plus une écriture.
  function scheduleAnnouncementHeight() {
    if (announcementQueued) return;
    announcementQueued = true;
    requestAnimationFrame(function () {
      announcementQueued = false;
      commitAnnouncementHeight();
    });
  }

  function initAnnouncementOffset() {
    announcementBar = document.querySelector('.announcement-bar-section');

    // Theme Editor : la section peut être re-rendue à chaud, on ne laisse pas
    // s'empiler les observateurs sur des noeuds détachés.
    if (announcementObserver) {
      announcementObserver.disconnect();
      announcementObserver = null;
    }

    if (!announcementBar) {
      announcementLast = 0;
      document.documentElement.style.setProperty('--ttl-announcement-height', '0px');
      return;
    }

    if ('ResizeObserver' in window) {
      announcementObserver = new ResizeObserver(scheduleAnnouncementHeight);
      announcementObserver.observe(announcementBar);
    }

    // Même référence de fonction à chaque appel -> addEventListener dédoublonne
    // de lui-même, aucun risque d'empilement en Theme Editor.
    window.addEventListener('scroll', scheduleAnnouncementHeight, { passive: true });
    window.addEventListener('resize', scheduleAnnouncementHeight);

    // Première valeur posée tout de suite : elle conditionne le placement du
    // header, on ne la fait pas attendre un frame.
    commitAnnouncementHeight();
  }

  /* ------------------------------------------------------------------
     Repli de la nav en burger
     ------------------------------------------------------------------ */

  var headerObserver = null;
  var headerResizeBound = null;

  function init() {
    var nav = document.querySelector('.section-header [data-ttl-nav]');
    if (!nav) return;

    var header = nav.closest('.header');
    var list = nav.querySelector('ul');
    var heading = header ? header.querySelector('.header__heading-link') : null;
    if (!header || !list || !heading) return;

    var requiredWidth = 0;
    var updateQueued = false;
    var recheckQueued = false;

    function measureRequired() {
      // Mesure fiable uniquement quand la nav est rendue (non repliée, desktop).
      // Somme des largeurs des items + gaps : indépendant de tout overflow,
      // contrairement à scrollWidth.
      if (header.classList.contains(COLLAPSED_CLASS)) return;
      var items = list.children;
      if (!items.length) return;

      var total = 0;
      for (var i = 0; i < items.length; i++) {
        total += items[i].getBoundingClientRect().width;
      }
      var listStyles = window.getComputedStyle(list);
      var gap = parseFloat(listStyles.columnGap) || 0;
      total += gap * (items.length - 1);

      if (total > 0) requiredWidth = Math.ceil(total);
    }

    function availableWidth() {
      var styles = window.getComputedStyle(header);
      var inner =
        header.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      // Grille middle-center : 1fr auto 1fr -> chaque côté = (total - logo) / 2
      return (inner - heading.offsetWidth) / 2;
    }

    function update() {
      // --- Phase de LECTURE : rien n'est écrit tant qu'on mesure. ---
      measureRequired();
      if (requiredWidth === 0) return; // nav jamais mesurée (mobile) : Dawn gère déjà le burger

      var available = availableWidth();
      var collapsed = requiredWidth + BUFFER > available;
      var wasCollapsed = header.classList.contains(COLLAPSED_CLASS);

      // Rien à faire : on sort AVANT toute écriture. C'est aussi ce qui empêche
      // le ResizeObserver de boucler sur notre propre changement de classe.
      if (wasCollapsed === collapsed) return;

      // --- Phase d'ÉCRITURE : une seule, en fin de fonction. ---
      header.classList.toggle(COLLAPSED_CLASS, collapsed);

      // On vient de déplier : la nav redevient visible, mais sa largeur réelle
      // n'est lisible qu'une fois la mise en page refaite. L'ancienne version
      // la lisait dans la foulée, ce qui forçait un reflow synchrone ; on
      // reporte au frame suivant. Le `wasCollapsed === collapsed` ci-dessus
      // arrête la récursion dès que l'état est stable.
      if (!collapsed && !recheckQueued) {
        recheckQueued = true;
        requestAnimationFrame(function () {
          recheckQueued = false;
          update();
        });
      }
    }

    // Les rafales de resize (glissement de fenêtre, rotation, apparition de la
    // barre d'URL mobile) sont ramenées à une passe par frame.
    function scheduleUpdate() {
      if (updateQueued) return;
      updateQueued = true;
      requestAnimationFrame(function () {
        updateQueued = false;
        update();
      });
    }

    if (headerObserver) {
      headerObserver.disconnect();
      headerObserver = null;
    }
    if (headerResizeBound) {
      window.removeEventListener('resize', headerResizeBound);
      headerResizeBound = null;
    }

    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(scheduleUpdate);
      headerObserver.observe(header);
    } else {
      headerResizeBound = scheduleUpdate;
      window.addEventListener('resize', headerResizeBound);
    }

    // Jost/Inter chargées en swap : la largeur de la nav change après coup
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleUpdate);
    }

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAnnouncementOffset();
      init();
    });
  } else {
    initAnnouncementOffset();
    init();
  }

  // Theme Editor : la section header peut être re-rendue à chaud
  document.addEventListener('shopify:section:load', function (event) {
    if (
      event.target.querySelector('[data-ttl-nav]') ||
      event.target.classList.contains('announcement-bar-section')
    ) {
      // Le noeud observé a pu être remplacé : on repart d'une mesure neuve.
      announcementLast = null;
      initAnnouncementOffset();
      init();
    }
  });
})();
